import { useState, useEffect, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import { useAuthStore } from "@/store/authStore";
import { sfxCoinBurn, sfxAchievement } from "@/utils/soundEngine";
import toast from "react-hot-toast";
import clsx from "clsx";

/* ─── Burn tier config ───────────────────────────────────────────────────── */
const BURN_TIERS = [
  {
    id:      "ignite",
    min:     2000,
    label:   "Zündung",
    emoji:   "🔥",
    reward:  "Seltenes Rang-Abzeichen + XP-Boost",
    xpBonus: 500,
    colorA:  "#f97316",
    colorB:  "#fb923c",
    glow:    "rgba(249,115,22,0.55)",
    desc:    "Der erste Funke.",
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" strokeWidth="1.8"
        stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2c0 0-5.5 5.5-5.5 11a5.5 5.5 0 0 0 11 0C17.5 7.5 12 2 12 2z"/>
        <path d="M12 8c0 0-2 3-2 5a2 2 0 0 0 4 0c0-2-2-5-2-5z"/>
      </svg>
    ),
  },
  {
    id:      "inferno",
    min:     10000,
    label:   "Inferno",
    emoji:   "💥",
    reward:  "Epischer Shop-Gutschein + Exklusiver Titel",
    xpBonus: 2500,
    colorA:  "#ef4444",
    colorB:  "#f87171",
    glow:    "rgba(239,68,68,0.6)",
    desc:    "Alles verbrennt.",
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" strokeWidth="1.8"
        stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2c0 0-7 7-7 13a7 7 0 0 0 14 0C19 9 12 2 12 2z"/>
        <path d="M12 8c0 0-3 3-3 6a3 3 0 0 0 6 0c0-3-3-6-3-6z"/>
      </svg>
    ),
  },
  {
    id:      "supernova",
    min:     50000,
    label:   "Supernova",
    emoji:   "⭐",
    reward:  "LEGENDÄRE Belohnung + Schulfrei-Ticket + Ewiger Ruhm",
    xpBonus: 12500,
    colorA:  "#fbbf24",
    colorB:  "#fde68a",
    glow:    "rgba(251,191,36,0.65)",
    desc:    "Legende für die Ewigkeit.",
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" strokeWidth="1.8"
        stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
      </svg>
    ),
  },
];

function getTier(amount) {
  for (let i = BURN_TIERS.length - 1; i >= 0; i--) {
    if (amount >= BURN_TIERS[i].min) return BURN_TIERS[i];
  }
  return null;
}

/* ─── Flame particle ─────────────────────────────────────────────────────── */
function FlameParticle({ color, x, delay, size }) {
  return (
    <div
      style={{
        position:  "absolute",
        bottom:    "0",
        left:      `${x}%`,
        width:     `${size}px`,
        height:    `${size}px`,
        borderRadius: "50% 50% 50% 0",
        background: `radial-gradient(circle, ${color}, transparent 70%)`,
        boxShadow: `0 0 ${size * 2}px ${color}`,
        animation: `burn-rise ${0.5 + Math.random() * 0.7}s ${delay}s ease-out forwards`,
        pointerEvents: "none",
        transformOrigin: "bottom center",
      }}
    />
  );
}

/* ─── Counter animation ──────────────────────────────────────────────────── */
function AnimatedCounter({ value, color }) {
  const [display, setDisplay] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    const start = Date.now();
    const duration = 1200;
    const tick = () => {
      const t = Math.min((Date.now() - start) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 4);
      setDisplay(Math.round(value * ease));
      if (t < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [value]);
  return (
    <span style={{ color, fontVariantNumeric: "tabular-nums" }}>
      {display.toLocaleString("de-DE")}
    </span>
  );
}

/* ─── Burn trigger threshold ─────────────────────────────────────────────── */
const FORCE_BURN_THRESHOLD = 2000;
export function useCoinBurnTrigger() {
  const { user } = useAuthStore();
  return (user?.studentProfile?.coins || 0) >= FORCE_BURN_THRESHOLD;
}

/* ─── Main CoinBurn component ────────────────────────────────────────────── */
export default function CoinBurn({ open, onClose }) {
  const { user, refreshMe } = useAuthStore();
  const coins  = user?.studentProfile?.coins || 0;
  const qc     = useQueryClient();

  const [amount,    setAmount]    = useState(Math.min(2000, coins));
  const [burning,   setBurning]   = useState(false);
  const [success,   setSuccess]   = useState(false);
  const [flames,    setFlames]    = useState([]);
  const [confirmed, setConfirmed] = useState(false);
  const burnResult = useRef(null);

  const tier = getTier(amount);

  // Inject keyframes once
  useEffect(() => {
    if (document.getElementById("burn-rise-kf")) return;
    const style = document.createElement("style");
    style.id = "burn-rise-kf";
    style.textContent = `
      @keyframes burn-rise {
        0%   { transform: translateY(0) scale(1) rotate(-15deg); opacity: 0.9; }
        60%  { opacity: 0.7; transform: translateY(-80px) scale(0.8) rotate(10deg); }
        100% { transform: translateY(-160px) scale(0) rotate(20deg); opacity: 0; }
      }
      @keyframes burn-shake {
        0%,100% { transform: translateX(0); }
        25%     { transform: translateX(-4px) rotate(-1deg); }
        75%     { transform: translateX(4px) rotate(1deg); }
      }
      @keyframes burn-in {
        from { opacity: 0; transform: translateY(24px) scale(0.95); }
        to   { opacity: 1; transform: translateY(0) scale(1); }
      }
    `;
    document.head.appendChild(style);
  }, []);

  const burnMutation = useMutation({
    mutationFn: (burnAmount) => api.post("/shop/burn-coins", { amount: burnAmount }),
    onSuccess: async (res) => {
      burnResult.current = res.data;
      sfxCoinBurn(amount >= 10000 ? 3 : amount >= 2000 ? 2 : 1);
      const t = tier || BURN_TIERS[0];
      const ps = Array.from({ length: 30 }, (_, i) => ({
        id: i, color: i % 3 === 0 ? t.colorB : i % 2 === 0 ? t.colorA : "#fbbf24",
        x: 5 + Math.random() * 90, delay: Math.random() * 0.5, size: 6 + Math.random() * 10,
      }));
      setFlames(ps);
      setSuccess(true);
      setBurning(false);
      await refreshMe();
      qc.invalidateQueries({ queryKey: ["student", "me"] });
      setTimeout(() => sfxAchievement(), 600);
      toast.success(`🔥 ${amount.toLocaleString("de-DE")} Coins verbrannt!`, { duration: 5000 });
    },
    onError: (err) => {
      setBurning(false);
      toast.error(err.response?.data?.error || "Fehler beim Verbrennen");
    },
  });

  const handleBurn = () => {
    if (amount < 2000) { toast.error("Mindestens 2 000 Coins nötig"); return; }
    if (amount > coins) { toast.error("Nicht genug Coins"); return; }
    setBurning(true);
    sfxCoinBurn(1);
    burnMutation.mutate(amount);
  };

  if (!open) return null;

  const activeTier = tier || null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-4"
      /* NO backdrop click to close — this module is intentionally unclosable during selection */
    >
      {/* Deep backdrop */}
      <div className="absolute inset-0 bg-black/90 backdrop-blur-md"/>

      {/* Main card */}
      <div
        className="relative z-10 w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl"
        style={{ animation: "burn-in 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards" }}
      >
        {/* Ambient glow behind card */}
        <div
          className="absolute -inset-4 pointer-events-none"
          style={{
            background: activeTier
              ? `radial-gradient(ellipse at 50% 100%, ${activeTier.glow} 0%, transparent 70%)`
              : "radial-gradient(ellipse at 50% 100%, rgba(249,115,22,0.3) 0%, transparent 70%)",
            filter: "blur(20px)",
          }}
        />

        {/* Card body */}
        <div
          className="relative"
          style={{
            background: "linear-gradient(180deg, #0f0a05 0%, #0a0510 100%)",
            border: `1px solid ${activeTier ? activeTier.colorA + "50" : "rgba(249,115,22,0.3)"}`,
            borderRadius: "24px",
          }}
        >
          {/* Animated top border */}
          <div
            className="absolute inset-x-0 top-0 h-px"
            style={{
              background: activeTier
                ? `linear-gradient(90deg, transparent, ${activeTier.colorA}, ${activeTier.colorB}, transparent)`
                : "linear-gradient(90deg, transparent, #f97316, #fbbf24, transparent)",
              animation: "holo-sweep 3s linear infinite",
            }}
          />

          {success ? (
            /* ── Success State ─────────────────────────────────────────────── */
            <div className="relative overflow-hidden p-8 text-center">
              {/* Flame particles */}
              <div className="absolute inset-x-0 bottom-0 h-32 pointer-events-none overflow-hidden">
                {flames.map((f) => <FlameParticle key={f.id} {...f}/>)}
              </div>

              {/* Glow orb */}
              <div className="absolute top-4 left-1/2 -translate-x-1/2 w-40 h-40 pointer-events-none"
                style={{ background: `radial-gradient(circle, ${tier?.glow || "rgba(249,115,22,0.4)"}, transparent 70%)`, filter: "blur(30px)" }}
              />

              <div className="relative z-10">
                <div
                  className="text-6xl mb-4 inline-block"
                  style={{ filter: `drop-shadow(0 0 30px ${tier?.colorA || "#f97316"})`, animation: "burn-shake 0.4s ease infinite" }}
                >
                  🔥
                </div>
                <h2 className="text-2xl font-black text-white mb-1">
                  {tier?.label || "Verbrannt!"}
                </h2>
                <p className="text-sm text-gray-400 mb-1">
                  <AnimatedCounter value={burnResult.current?.burned || amount} color={tier?.colorA || "#f97316"} />
                  {" "}Coins für immer vernichtet
                </p>
                {tier && (
                  <div
                    className="mt-4 p-3.5 rounded-2xl border text-sm font-bold text-center"
                    style={{ borderColor: `${tier.colorA}50`, background: `${tier.colorA}12`, color: tier.colorA }}
                  >
                    <div className="text-lg mb-0.5">+{tier.xpBonus.toLocaleString()} XP</div>
                    <div className="text-[11px] opacity-70 font-medium">{tier.reward}</div>
                  </div>
                )}
                <button
                  onClick={() => { setSuccess(false); setConfirmed(false); setFlames([]); onClose(); }}
                  className="mt-5 w-full py-3 rounded-2xl text-sm font-black transition-all duration-200"
                  style={{
                    background: `linear-gradient(135deg, ${tier?.colorA || "#f97316"}, ${tier?.colorB || "#fb923c"})`,
                    boxShadow: `0 0 20px ${tier?.glow || "rgba(249,115,22,0.4)"}`,
                    color: "#fff",
                  }}
                >
                  Episch. Weiter.
                </button>
              </div>
            </div>
          ) : (
            /* ── Main State ────────────────────────────────────────────────── */
            <div>
              {/* Header */}
              <div className="relative px-5 pt-5 pb-4 overflow-hidden">
                <div className="absolute inset-0 pointer-events-none"
                  style={{ background: "linear-gradient(180deg, rgba(249,115,22,0.08) 0%, transparent 100%)" }}/>

                <div className="relative flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center"
                      style={{ background: "linear-gradient(135deg, #f97316, #ef4444)", boxShadow: "0 0 16px rgba(249,115,22,0.5)" }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="white" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2c0 0-5.5 5.5-5.5 11a5.5 5.5 0 0 0 11 0C17.5 7.5 12 2 12 2z"/>
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-base font-black text-white">Coin-Verbrennung</h2>
                      <p className="text-[10px] text-orange-400/60 font-medium">Vernichte Coins. Ernte Belohnungen.</p>
                    </div>
                  </div>
                  {/* Close only when NOT burning */}
                  {!burning && (
                    <button
                      onClick={onClose}
                      className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-white/5 transition-colors"
                      style={{ color: "rgba(255,255,255,0.3)" }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" stroke="currentColor" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                    </button>
                  )}
                </div>
              </div>

              <div className="px-5 pb-5 space-y-4">
                {/* Balance card */}
                <div
                  className="flex items-center justify-between p-4 rounded-2xl border"
                  style={{ background: "rgba(251,191,36,0.04)", borderColor: "rgba(251,191,36,0.15)" }}
                >
                  <div>
                    <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-0.5">Dein Guthaben</p>
                    <div className="flex items-center gap-1.5">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" stroke="#fbbf24" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/><path d="M12 6v12M9 9h4.5a1.5 1.5 0 0 1 0 3H9m0 3h5.5a1.5 1.5 0 0 0 0-3"/>
                      </svg>
                      <span className="text-xl font-black text-yellow-400">{coins.toLocaleString("de-DE")}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-gray-500 mb-0.5">Verbrennen</p>
                    <span className="text-xl font-black" style={{ color: tier?.colorA || "#f97316" }}>
                      {amount.toLocaleString("de-DE")}
                    </span>
                  </div>
                </div>

                {/* Tier selector */}
                <div className="grid grid-cols-3 gap-2">
                  {BURN_TIERS.map((t) => {
                    const active = activeTier?.id === t.id;
                    const canUse = coins >= t.min;
                    return (
                      <button
                        key={t.id}
                        onClick={() => canUse && setAmount(t.min)}
                        disabled={!canUse}
                        className="flex flex-col items-center gap-1 p-3 rounded-xl border transition-all duration-200"
                        style={{
                          borderColor: active ? t.colorA : canUse ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.03)",
                          background:  active ? `${t.colorA}18` : canUse ? "rgba(255,255,255,0.02)" : "transparent",
                          color:       active ? t.colorA : canUse ? t.colorA + "aa" : "#444",
                          boxShadow:   active ? `0 0 12px ${t.glow}` : "none",
                          opacity:     canUse ? 1 : 0.3,
                        }}
                      >
                        <div style={{ color: active ? t.colorA : undefined }}>{t.icon}</div>
                        <span className="text-[10px] font-black">{t.label}</span>
                        <span className="text-[8px] text-gray-600">{t.min.toLocaleString()}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Amount slider */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Menge wählen</label>
                    <span className="text-xs font-black" style={{ color: tier?.colorA || "#f97316" }}>
                      {amount.toLocaleString("de-DE")} Coins
                    </span>
                  </div>
                  <input
                    type="range" min={2000} max={Math.max(coins, 2000)} step={100} value={amount}
                    onChange={(e) => setAmount(Number(e.target.value))}
                    className="w-full h-2 rounded-full appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(90deg, ${tier?.colorA || "#f97316"} ${(amount / Math.max(coins, 2000)) * 100}%, rgba(255,255,255,0.06) ${(amount / Math.max(coins, 2000)) * 100}%)`,
                    }}
                  />
                  <div className="flex justify-between text-[9px] text-gray-700 mt-1">
                    <span>2 000 min</span>
                    <span>{coins.toLocaleString("de-DE")} max</span>
                  </div>
                </div>

                {/* Active tier reward */}
                {activeTier ? (
                  <div
                    className="flex items-center gap-3 p-4 rounded-2xl border"
                    style={{ borderColor: `${activeTier.colorA}35`, background: `${activeTier.colorA}08` }}
                  >
                    <div style={{ color: activeTier.colorA }}>{activeTier.icon}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-black" style={{ color: activeTier.colorA }}>
                        {activeTier.label} — {activeTier.desc}
                      </p>
                      <p className="text-[10px] text-gray-500 truncate">{activeTier.reward}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-black text-green-400">+{activeTier.xpBonus.toLocaleString()}</p>
                      <p className="text-[9px] text-gray-600">XP Bonus</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-2">
                    <p className="text-xs text-gray-600">
                      Mindestens <span className="text-orange-400 font-bold">2 000 Coins</span> für eine Belohnung
                    </p>
                  </div>
                )}

                {/* Warning */}
                <div className="flex items-start gap-2.5 p-3 rounded-xl"
                  style={{ background: "rgba(239,68,68,0.05)", border: "1px solid rgba(239,68,68,0.15)" }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="#ef4444" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                  <p className="text-[10px] text-red-400/80 leading-relaxed">
                    Verbrannte Coins sind <strong className="text-red-400">für immer weg</strong> und können nicht zurückgegeben werden.
                  </p>
                </div>

                {/* Burn button */}
                <button
                  onClick={handleBurn}
                  disabled={burning || amount < 2000 || amount > coins}
                  className={clsx(
                    "w-full py-4 rounded-2xl text-sm font-black transition-all duration-300 relative overflow-hidden",
                    burning || amount < 2000 || amount > coins
                      ? "bg-gray-900 text-gray-600 cursor-not-allowed border border-gray-800"
                      : "text-white active:scale-[0.98]"
                  )}
                  style={
                    !(burning || amount < 2000 || amount > coins) ? {
                      background: activeTier
                        ? `linear-gradient(135deg, ${activeTier.colorA}, ${activeTier.colorB})`
                        : "linear-gradient(135deg, #f97316, #ef4444)",
                      boxShadow: activeTier
                        ? `0 0 25px ${activeTier.glow}, 0 4px 15px rgba(0,0,0,0.3)`
                        : "0 0 20px rgba(249,115,22,0.4)",
                    } : undefined
                  }
                >
                  {/* Shine sweep */}
                  {!burning && amount >= 2000 && amount <= coins && (
                    <div className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity pointer-events-none"
                      style={{ background: "linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.2) 50%, transparent 70%)" }}
                    />
                  )}
                  {burning ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor">
                        <circle cx="12" cy="12" r="10" opacity="0.3"/><path d="M12 2a10 10 0 0 1 10 10"/>
                      </svg>
                      Verbrennt…
                    </span>
                  ) : `🔥 ${amount.toLocaleString("de-DE")} Coins verbrennen`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
